import { useCallback, useEffect, useState } from 'react';

export interface AudioDevice {
  deviceId: string;
  label: string;
}

/**
 * Enumerate the browser's audio input (microphone) and output (speaker) devices for the voice
 * controls' device picker. Refreshes on `devicechange` (plug/unplug). Labels are only populated
 * once the user has granted mic permission — until then we fall back to generic labels.
 *
 * `outputSupported` reflects whether the browser can switch the *output* device for Web Audio
 * playback (`AudioContext.setSinkId`, Chromium-desktop only). On iOS/Safari output routing is
 * controlled by the OS and cannot be changed from the page.
 */
export function useAudioDevices(): {
  inputs: AudioDevice[];
  outputs: AudioDevice[];
  outputSupported: boolean;
  refresh: () => Promise<void>;
} {
  const [inputs, setInputs] = useState<AudioDevice[]>([]);
  const [outputs, setOutputs] = useState<AudioDevice[]>([]);

  const refresh = useCallback(async () => {
    try {
      const devs = await navigator.mediaDevices.enumerateDevices();
      const pick = (kind: MediaDeviceKind, fallback: string): AudioDevice[] =>
        devs
          .filter((d) => d.kind === kind)
          .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `${fallback} ${i + 1}` }));
      setInputs(pick('audioinput', 'Microphone'));
      setOutputs(pick('audiooutput', 'Speaker'));
    } catch {
      // enumerateDevices unavailable (insecure context / unsupported) — leave lists empty.
    }
  }, []);

  useEffect(() => {
    refresh();
    const md = navigator.mediaDevices;
    md?.addEventListener?.('devicechange', refresh);
    return () => md?.removeEventListener?.('devicechange', refresh);
  }, [refresh]);

  const outputSupported =
    typeof window !== 'undefined' &&
    typeof (window as { AudioContext?: typeof AudioContext }).AudioContext !== 'undefined' &&
    typeof (window.AudioContext?.prototype as unknown as { setSinkId?: unknown })?.setSinkId === 'function';

  return { inputs, outputs, outputSupported, refresh };
}
